import { useTranslation } from "react-i18next";
import {
  useState,
  useEffect,
  useMemo,
  useRef
} from "react";
import {
  useNavigate,
  useLocation
} from "react-router-dom";
import { useDispatch } from "react-redux";

import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';
import {
  PsSelectTuner,
  PsTextTuner,
  PsButtonTuner,
  PsSwitchTuner
} from "stencilSetup";
import {
  useAccountDetailsQuery,
  GET_ACCOUNT_DETAILS_QUERY_KEY,
} from "queries/useAccountDetailsQuery";
import {
  MfaMethodDto,
  MFA_GENERATE_EMAIL_VERIFICATION_CODE_KEY,
  MFA_UPDATE_EMAIL_METHOD_KEY,
  MFA_UPDATE_SMS_METHOD_KEY,
  MFA_UPDATE_PREFERRED_METHOD_KEY,
  SMS_METHOD_CODE,
  EMAIL_METHOD_CODE,
  EmailUserMethodRequestDto,
  SmsUserMethodRequestDto,
  UserMethodPreferredRequestDto,
} from "./utils";
import { MfaMethodForm } from "./components";
import { useConfigurationQuery } from "theSource/queries";
import { setAuthenticated } from "theSource/features";
import { useApiMutation, queryClient } from "theSource/utils";
import { ResponseDto, ErrorResponseDto } from "theSource/types";
import { LOGOFF_QUERY_KEY } from 'theSource/components/Header/components/utils';
import { OrbitModal } from "enrollment/components/Shared/OrbitModal";

declare const bootstrap: any;

interface ConfigureMfaLocationState {
  fromLogin: boolean
}

export const ConfigureMFA = () => {
  const { t } = useTranslation('pages.configureMfa');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToastDialog();

  const location = useLocation() as { state: ConfigureMfaLocationState };
  const fromLogin = location.state?.fromLogin || false;

  const [useMfa, setUseMfa] = useState(false);
  // eslint-disable-next-line
  const [mfaRequired, setMfaRequired] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<MfaMethodDto | undefined>({ code: '', name: 'methodNone', preferred: false });
  const [isEditing, setIsEditing] = useState(false);
  const [hasUpdatedConfiguration, setHasUpdatedConfiguration] = useState(false);

  const mfaCollapseRef = useRef<HTMLDivElement>(null);
  const mfaSelectRef = useRef<HTMLDivElement>(null);
  const verificationCodeRef = useRef<HTMLPsTextTunerElement>(null);
  const emailInputRef = useRef<HTMLPsTextTunerElement>(null);
  const smsInputRef = useRef<HTMLPsTextTunerElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { configuration } = useConfigurationQuery();

  const {
    data: accountDetails
  } = useAccountDetailsQuery({
    enabled: true,
  });

  const enableFinish = useMemo(() => useMfa && hasUpdatedConfiguration, [useMfa, hasUpdatedConfiguration]);

  const mfaConfiguration = accountDetails?.data?.mfa_configuration;

  const mfaMethods = useMemo(() => mfaConfiguration?.methods || [], [mfaConfiguration]);
  const configured = useMemo(() => mfaConfiguration?.configured || false, [mfaConfiguration]);
  const required = useMemo(() => mfaConfiguration?.required || false, [mfaConfiguration]);
  const preferredMethod = useMemo(() => mfaMethods.find((method) => method.preferred), [mfaMethods]);

  useEffect(() => {
    setUseMfa((configured && Boolean(preferredMethod)) || required);
    setMfaRequired(required);

    if (configured) {
      setSelectedMethod(preferredMethod);
    }
  }, [configured, preferredMethod, required]);

  const { mutate: generateEmailVerificationCode } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    EmailUserMethodRequestDto
  >({
    queryKey: MFA_GENERATE_EMAIL_VERIFICATION_CODE_KEY,
    method: 'post',
    endpoint: `${configuration?.apiHost}/v1/self_service/mfa_setup/email_user_method`,
    onSuccess: () => {
      setIsModalOpen(true);
    },
    getErrorMessage: (error: any) => error.response?.data?.details,
    showSuccessSnackbar: false,
    showErrorSnackbar: true,
    toastRef: toast
  });

  const { mutate: updateEmailUserMethodEndpoint } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    EmailUserMethodRequestDto
  >({
    queryKey: MFA_UPDATE_EMAIL_METHOD_KEY,
    method: 'put',
    queryKeysToInvalidate: [GET_ACCOUNT_DETAILS_QUERY_KEY],
    endpoint: `${configuration?.apiHost}/v1/self_service/mfa_setup/email_user_method`,
    onSuccess: () => {
      updatePreferredUserMethodEndpoint({ code: EMAIL_METHOD_CODE });
      setIsEditing(false);
      setHasUpdatedConfiguration(true);
    },
    getErrorMessage: (error: any) => error.response?.data?.details,
    showSuccessSnackbar: false,
    showErrorSnackbar: true,
    toastRef: toast
  });

  const { mutate: updateSmsUserMethodEndpoint } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    SmsUserMethodRequestDto
  >({
    queryKey: MFA_UPDATE_SMS_METHOD_KEY,
    method: 'post',
    queryKeysToInvalidate: [GET_ACCOUNT_DETAILS_QUERY_KEY],
    endpoint: `${configuration?.apiHost}/v1/self_service/mfa_setup/sms_user_method`,
    onSuccess: () => {
      updatePreferredUserMethodEndpoint({ code: SMS_METHOD_CODE });
      setIsEditing(false);
      setHasUpdatedConfiguration(true);
    },
    getErrorMessage: (error: any) => error.response?.data?.details,
    showSuccessSnackbar: false,
    showErrorSnackbar: true,
    toastRef: toast
  });

  const { mutate: updatePreferredUserMethodEndpoint } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    UserMethodPreferredRequestDto
  >({
    queryKey: MFA_UPDATE_PREFERRED_METHOD_KEY,
    method: 'post',
    endpoint: `${configuration?.apiHost}/v1/self_service/mfa_setup/user_method_preferred`,
    onSuccess: () => {
      queryClient.invalidateQueries(
        GET_ACCOUNT_DETAILS_QUERY_KEY
      );
      setIsEditing(false);
      setHasUpdatedConfiguration(true);
    },
    getErrorMessage: (error: any) => error.response?.data?.details,
    showSuccessSnackbar: false,
    showErrorSnackbar: true,
    toastRef: toast
  });

  const { mutate: logoff } = useApiMutation<{}, {}, {}>({
    queryKey: LOGOFF_QUERY_KEY,
    clearQueryCache: true,
    method: 'put',
    endpoint: '/logoff',
    onSuccess: () => {

      navigate('/login');
    },
    showSuccessSnackbar: false,
    snackbarOptions: {
      position: 'bottom',
    },
    toastRef: toast
  });

  // need to ensure can't navigate away if redirected from the login page.
  // haven't successfully logged in with MFA yet.
  useEffect(() => {
    if (fromLogin) {
      dispatch(setAuthenticated(false));
    }
  }, [fromLogin, dispatch]);

  const handleMFASelect = (e: CustomEvent<{checked: boolean; value: boolean}>) => {
    const mfaElement = mfaCollapseRef.current;
    if (mfaElement) {
      const collapse = new bootstrap.Collapse(mfaElement, { toggle: false });
      if (e.detail.checked) {
        collapse.show();
      } else {
        collapse.hide();
      }
    }
  };

  const handleChosenMethod = (e: CustomEvent<{checked: boolean; value: string; text: string}>) => {
    const methodName = e.detail.text;
    const method = mfaMethods.find((method) => method.name === methodName);
    
    setSelectedMethod(method);
    setIsEditing(false);

    const chosenMethodElement = mfaSelectRef.current;
    if (chosenMethodElement) {
      const collapse = new bootstrap.Collapse(chosenMethodElement, { toggle: false });
      // Show collapse if value is a non-empty string, hide otherwise
      if (e.detail.value && typeof e.detail.value === 'string' && e.detail.value.length > 0) {
        collapse.show();
      } else {
        collapse.hide();
      }
    }
  };

  useEffect(() => {
    // Wait for the refs to be available and Bootstrap to be loaded
    if (mfaCollapseRef.current && mfaSelectRef.current && useMfa) {
      // Initialize both collapses
      const mfaCollapse = new bootstrap.Collapse(mfaCollapseRef.current, { toggle: false });
      const chosenMethodCollapse = new bootstrap.Collapse(mfaSelectRef.current, { toggle: false });

      // Show both collapses if useMfa is true
      mfaCollapse.show();
      
      // Only show chosen_method if a method is selected
      if (selectedMethod && selectedMethod.code) {
        chosenMethodCollapse.show();
      }
    }
  }, [useMfa, selectedMethod]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (selectedMethod?.code === 'email') {
      const email = await emailInputRef.current?.getValue() || '';
      const registeredEmails = mfaMethods.find((method) => method.code === 'email')?.data?.emails || [];
      const emailRegistered = registeredEmails.some((registeredEmail) => registeredEmail.email === email);
      
      if (emailRegistered) {
        updatePreferredUserMethodEndpoint({ code: EMAIL_METHOD_CODE });
      } else {
        generateEmailVerificationCode({
          email: email,
        });
      }
    } else if (selectedMethod?.code === 'sms') {
      const phoneNumber = await smsInputRef.current?.getValue() || '';
      const registeredNumbers = mfaMethods.find((method) => method.code === 'sms')?.data?.phones || [];
      const numberRegistered = registeredNumbers.some((registeredNumber) => registeredNumber.mobile_number === phoneNumber);

      if (numberRegistered) {
        updatePreferredUserMethodEndpoint({ code: SMS_METHOD_CODE });
      } else {
        updateSmsUserMethodEndpoint({
          mobile_number: phoneNumber,
          preferred: true,
          active: true,
        });
      }
    }
  };

  const handleSubmitCode = async () => {
    const verificationCode = await verificationCodeRef.current?.getValue() || '';
    const email = await emailInputRef.current?.getValue() || '';

    updateEmailUserMethodEndpoint({
      email: email,
      preferred: true,
      active: true,
      code: verificationCode,
    });

    setIsModalOpen(false);
  };

  const handleNavigate = () => {
    if (fromLogin) {
      logoff({});
    } else {
      navigate('/');
    }
  };

  return (
    <div>
      <h1>{t('title')}</h1>
      <p>{t('description')}</p>
      
      <div className="my-4">
        <PsSwitchTuner
          switchId="mfa_switch"   
          data-testid="mfa_switch"
          label={t('useMfa')}
          checked={useMfa}
          value={useMfa}
          onPsSwitchChange={handleMFASelect}
        />
      </div>

      <div id="mfa_collapse" className="collapse" ref={mfaCollapseRef}>
        <PsSelectTuner
          inputId="mfa_select"
          data-testid="mfa_select"
          label="MFA Select"
          firstOption={true}
          onPsChange={handleChosenMethod}
        >
          <option
            key="mfaMethod-none"
            id="mfaMethod-none"
            value=""
            selected={true}
          />
          {mfaMethods.length > 0 && mfaMethods.map((method, index) => {
            if (method.code === 'backup_codes') {
              return null;
            }
            return (
              <option
                key={`mfaMethod-${index}`}
                value={`mfaMethod-${index}`}
                {...(selectedMethod?.code === method.code ? { selected: true } : '')}
              >
                {method.name}
              </option>
            );
          })}
        </PsSelectTuner>

        <div id="chosen_method" className="collapse" ref={mfaSelectRef}>
          <MfaMethodForm
            selectedMethod={selectedMethod}
            isEditing={isEditing}
            handleEdit={handleEdit}
            handleSave={handleSave}
            emailInputRef={emailInputRef}
            smsInputRef={smsInputRef}
          />
        </div>
      </div>

      <div className="mt-5">
        <div className="mt-5">
          <PsButtonTuner
            data-testid="save-mfa-settings"
            buttonId="save-mfa-settings"
            label={t('finish')}
            level="primary"
            disabled={!enableFinish}
            onClick={handleNavigate}
          />
        </div>
        <OrbitModal
          data-testid="mfa-verification-modal"
          modalId="mfa-verification-modal"
          title={t('verifyEmail')}
          isOpen={isModalOpen}
          primaryActive={true}
          primaryButtonLabel={t('verify')}
          secondaryButtonLabel={t('cancel')}
          handleClose={() => setIsModalOpen(false)}
          handleSubmit={handleSubmitCode}
        >
          <>
            {t('verifyEmailDescription')}
            <PsTextTuner
              data-testid="email-verification-code"
              inputId="email-verification-code"
              name="email_verification_code"
              label=' '
              type="text"
              otherData={[{ attribute: 'autoComplete', value: 'off' }]}
              extraClass="mt-4"
              ref={verificationCodeRef}
            />
          </>
        </OrbitModal>
      </div>
    </div>
  );
};
